import { Component, Input, ViewChild, ElementRef, Injector, AfterViewInit, OnDestroy, ViewContainerRef, OnInit } from '@angular/core';
import { NavPanelConfig } from '../../interface/nav-panel';
import { ContextService } from '../../services/context.service';
import { ViewContainer } from '../view-container-package/view-container';
import { BaseWidget, UnsafeWidgetUtilities } from '../view-container-package/widegt';
import { NavPanelFactory } from './nav-panel.factory';
import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
import { EditorController } from '../../controller/editor.controller';
import { debounce } from 'lodash-es';
import { PresetConfigService } from '../../services/preset-config.service';

/** 左侧导航组件 */
@Component({
  selector: 'webide-nav-panel',
  templateUrl: './nav-panel.component.html'
})
export class NavPanelComponent implements OnInit, AfterViewInit, OnDestroy {

  /**
   * 导航面板配置
   * @remarks 初始化完成后忽略后续的配置变化
   */
  @Input() set navPanelConfigs(configs: NavPanelConfig[]) {
    this._navPanelConfigs = configs;
    this.update();
  }

  /**
   * 是否使用预置的导航面板配置信息
   * @remarks 本参数的优先级高于navPanelConfigs参数
   */
  @Input() set usePresetConfigs(useOrNot: boolean) {
    if (!this.frozen) {
      this._usePresetConfigs = useOrNot;
    }
  }

  get usePresetConfigs(): boolean {
    return this._usePresetConfigs;
  }

  get navPanelConfigs(): NavPanelConfig[] {
    return this._navPanelConfigs;
  }

  /** 渲染完成后不支持修改面板配置 */
  private initialized: boolean = false;
  /** 初始化完成后忽略usePresetConfigs参数的变化 */
  private frozen: boolean = false;
  private _navPanelConfigs: NavPanelConfig[];
  private _usePresetConfigs: boolean = false;

  @ViewChild("wrapper") wrapper: ElementRef;

  /** 导航分隔面板组件 */
  private panel: ViewContainer;

  private debouncedHandleResize: () => void;
  private debouncedHandleWrapperResize: () => void;

  constructor(
    private injector: Injector,
    private context: ContextService,
    private controller: EditorController,
    private navPanelFactory: NavPanelFactory,
    private viewContainerRef: ViewContainerRef,
    private presetConfigService: PresetConfigService
  ) {
    this.context.entryFilePath.subscribe(() => {
      this.update();
    });
    this.debouncedHandleResize = debounce(this.handleResize, 150, { leading: true, trailing: true, maxWait: 1000 });
    this.debouncedHandleWrapperResize = debounce(this.handleWrapperResize, 150, { leading: true, trailing: true, maxWait: 1000 });
  }

  ngOnInit(): void {
    this.frozen = true;
    this.update();
  }

  private update(): void {
    if (!this.frozen || this.initialized) {
      return;
    }
    if (this.usePresetConfigs) {
      const configProvider = this.presetConfigService.getPresetConfigProvider();
      const presetNavConfigs = configProvider && configProvider.getNavPanelConfig();
      if (presetNavConfigs) {
        this.renderNavPanels(presetNavConfigs);
        this.initialized = true;
      }
      return;
    }
    if (this.navPanelConfigs && this.navPanelConfigs.length > 0) {
      this.initialized = true;
      this.renderNavPanels(this.navPanelConfigs);
      return;
    }
  }

  /** 根据当前的导航面板配置信息动态渲染相关组件 */
  private renderNavPanels(configs: NavPanelConfig[]): void {
    if (!configs || configs.length === 0) {
      return;
    }
    // 初始化收折面板组件
    this.panel = new ViewContainer();
    // 按照配置依次生成每一个导航面板
    for (const config of configs) {
      const navPanel = new BaseWidget();
      navPanel.id = config.id;
      navPanel.title.label = config.title || "";
      navPanel.addClass("app-full-height");
      // 如果隐藏了标题栏，则应该为主体内容设置一个上边距
      if (!!config.hideTitleBar) {
        const paddingTop = typeof config.marginTopWhenHideTitleBar === 'number' ? config.marginTopWhenHideTitleBar : 8;
        navPanel.node.style.paddingTop = paddingTop + 'px';
      }
      let customScrollContainerEl: HTMLElement = null;
      let customScrollEl: HTMLElement = null;
      let navTreeCmp = null;
      if (!!config.templateRef) {
        // 如果采用自定义内容模板，则不再生成默认的导航树组件
        const viewRef = this.viewContainerRef.createEmbeddedView(config.templateRef, config.templateRefContext);
        const nodes = viewRef.rootNodes;
        for (const node of nodes) {
          node && navPanel.node.appendChild(node);
        }
      } else {
        // 自定义模板为空，生成通用文件导航树组件作为面板主体
        const isMainNavTree = config.isMainNavTree === false ? false : true;
        // 为导航树服务设置控制器引用
        config.navTreeDataService && (config.navTreeDataService['editorController'] = this.controller);
        // 创建导航树组件或者设置自定义导航组件
        const navTreeRef = this.navPanelFactory.createCommonFileNav(
          this.injector,
          config.navTreeDataService,
          config.navTreeNodeIconService,
          isMainNavTree,
          config.emptyMessage,
          config.searchBarConfig,
          config.virtualized,
          config.disableSearchService
        );
        navTreeCmp = navTreeRef.instance;
        customScrollContainerEl = navTreeRef.instance.getScrollContainer();
        customScrollEl = navTreeRef.instance.getScrollElement();
        navPanel.node.appendChild(navTreeRef.location.nativeElement);
        // 执行属性设置完成回调方法，通知服务editorController等属性已被设置，可以使用了
        try {
          config.navTreeDataService && config.navTreeDataService.afterPropertySet();
        } catch (err) { }
      }
      // 监听内容组件大小变化事件，实时更新滚动条大小
      const navCmpEl = customScrollEl || navPanel.node;
      addResizeListener(navCmpEl, this.debouncedHandleResize);
      navPanel.onDidDispose(() => {
        navCmpEl && removeResizeListener(navCmpEl, this.debouncedHandleResize);
      });
      // 如果存在工具栏配置则创建工具栏组件
      const hideTitleBar = config.hideTitleBar === true ? true : false;
      if (!!config.toolbarConfigService) {
        config.toolbarConfigService['editorController'] = this.controller;
        config.toolbarConfigService['navTreeCmp'] = navTreeCmp;
        try {
          config.toolbarConfigService.afterPropertySet();
        } catch (err) { }
        const toolbarRef = this.navPanelFactory.createToolbar(this.injector, config.toolbarConfigService);
        this.panel.addWidget(navPanel, toolbarRef.instance, { hideTitleBar, customScrollContainerEl });
      } else {
        this.panel.addWidget(navPanel, null, { hideTitleBar, customScrollContainerEl });
      }
    }
    UnsafeWidgetUtilities.attach(this.panel, this.wrapper.nativeElement);
  }

  private handleResize = () => {
    this.panel && this.panel.updateAllScrollbar();
  };

  private handleWrapperResize = () => {
    this.panel && this.panel.update();
  };

  ngAfterViewInit(): void {
    addResizeListener(this.wrapper.nativeElement, this.debouncedHandleWrapperResize);
  }

  ngOnDestroy(): void {
    removeResizeListener(this.wrapper.nativeElement, this.debouncedHandleWrapperResize);
  }

}
